import { useState, useMemo, useEffect, useRef } from "react";
import {
    Table2, Link2, ChevronRight, ArrowRight, ArrowLeft, Sparkles, Plus, Check,
    GripVertical, Info, FileSpreadsheet, Loader2, Search as SearchIcon, CircleSlash,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext, arrayMove,
    verticalListSortingStrategy, horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { toast } from "sonner";
import { useSizing, findTable, countLinked } from "@/lib/sizing-store";
import { gridToValueMap, gridSmartFill, gridPaste, remapValuesAfterReorder } from "../table/grid-helpers";
import { SortableGridRow, SortableColHeader } from "../table/SortableGridComponents";
import { LinkProductsModal } from "../dialogs/LinkProductsModal";
import { WizardStep1 } from "./WizardStep1";
import { WizardStep2 } from "./WizardStep2";
import { WizardLinkedProducts } from "./WizardLinkedProducts";

/**
 * AddTableWizard — 3-step drawer for creating (or linking) a sizing table.
 *
 * Modes:
 * • choice  — first-time entry point when opened FROM a product. Merchant
 *             picks between "Mevcut tabloya bağla" (`link` mode), "Yeni
 *             tablo oluştur" (`create` mode) and "Beden tablosu gerekmez"
 *             (mark the product as exempt).  Skipped when `standalone` is
 *             true (opened from the header CTA).
 * • link    — search existing tables, pick one, commit the link on save.
 * • create  — 3-step wizard: pick series+sizes, group+measures, then fill
 *             cm values. Drag-reorder rows/columns supported.  Multi-product
 *             linking is available in BOTH standalone and product-triggered
 *             flows (product is pre-selected in the picked list).
 *
 * State machine — mode → step is one-way (except back button on step >1).
 * Every state resets on close so re-opening starts fresh.
 */
export const AddTableWizard = ({ product, standalone = false, initialPickedIds, open, onClose }) => {
    const { series, groups, tables, links, products, addTable, linkProduct, setProductSizingExempt } = useSizing();
    const [mode, setMode] = useState("choice"); // choice | link | create
    const [step, setStep] = useState(1);
    const [tableName, setTableName] = useState("");
    const [linkedTableId, setLinkedTableId] = useState(null);
    const [linkQuery, setLinkQuery] = useState("");
    const [seriesId, setSeriesId] = useState(null);
    const [pickedSizes, setPickedSizes] = useState([]);
    const [groupId, setGroupId] = useState(null);
    const [pickedMeasures, setPickedMeasures] = useState([]);
    const [values, setValues] = useState({});
    const [confirmEmpty, setConfirmEmpty] = useState(false);
    // Locks the save CTA + "Yine de kaydet" while the /tables POST (and any
    // dependent /link calls) are in flight — prevents double-submit on rapid
    // clicks. Also acts as a visual signal ("Kaydediliyor…" + spinner).
    const [saving, setSaving] = useState(false);
    // Existing-catalog products the wizard will link to the table on save.
    // Same UX as the Edit-drawer LinkProductsModal.
    const [pickedProductIds, setPickedProductIds] = useState(new Set());
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    // Safety confirmation for the "Beden tablosu gerekmez" toggle when the
    // product currently has a table linked — mirrors the bulk-mode dialog so
    // single vs bulk behaviour stays consistent.
    const [confirmSingleExempt, setConfirmSingleExempt] = useState(false);
    const cellRefs = useRef({});

    // Standalone: no product context — skip choice screen, jump straight into create flow.
    // Also seed pickedProductIds from `initialPickedIds` (bulk create from
    // BulkActionBar → wizard's Bağlı Ürünler starts pre-populated).
    useEffect(() => {
        if (!open) return;
        if (standalone) { setMode("create"); setStep(1); }
        if (initialPickedIds && initialPickedIds.size > 0) {
            setPickedProductIds(new Set(initialPickedIds));
        }
    }, [standalone, open, initialPickedIds]);

    const reset = () => {
        setMode("choice"); setStep(1);
        setTableName("");
        setLinkedTableId(null);
        setLinkQuery("");
        setSeriesId(null); setPickedSizes([]);
        setGroupId(null); setPickedMeasures([]);
        setValues({});
        setPickedProductIds(new Set());
        setLinkModalOpen(false);
        setConfirmSingleExempt(false);
        setSaving(false);
    };
    const handleClose = () => { reset(); onClose(); };

    // Toggle helper — extracted so both the direct path and the safety-confirm
    // path fire the same snapshot + undo pattern. Snapshots the previous
    // exempt flag AND any tableId that would be cleared server-side so the
    // Geri Al toast can restore both.
    const applyExemptToggle = (nextExempt) => {
        if (!product) return;
        const snapshot = {
            id: product.id,
            prevExempt: !!product.sizingTableExempt,
            prevTableId: links[product.id] || null,
        };
        setProductSizingExempt(product.id, nextExempt);
        toast.success(
            nextExempt
                ? `${product.name || product.id} için beden tablosu gerekmiyor olarak işaretlendi`
                : `${product.name || product.id} artık beden tablosu bekliyor`,
            {
                duration: 5000,
                action: {
                    label: "Geri Al",
                    onClick: () => {
                        setProductSizingExempt(snapshot.id, snapshot.prevExempt);
                        if (snapshot.prevTableId) linkProduct(snapshot.id, snapshot.prevTableId);
                        toast.success(`${product.name || product.id} eski haline döndü`);
                    },
                },
            }
        );
        handleClose();
    };

    const selectedSeries = series.find((s) => s.id === seriesId) || null;
    const selectedGroup = groups.find((g) => g.id === groupId) || null;

    // The wizard now supports drag-drop reordering of sizes/measures in step 3,
    // so we honour the merchant's picked-order directly instead of forcing
    // the source series/group order. `pickedSizes` is a plain array that
    // preserves selection sequence (see `toggleSize` / `pickAllSizes`).
    const orderedSizes = useMemo(
        () => selectedSeries ? pickedSizes.filter((sz) => selectedSeries.sizes.includes(sz)) : [],
        [selectedSeries, pickedSizes]
    );
    const orderedMeasures = useMemo(() => {
        if (!selectedGroup) return [];
        const src = selectedGroup.measures;
        return pickedMeasures
            .map((name) => src.find((m) => m.name === name))
            .filter(Boolean);
    }, [selectedGroup, pickedMeasures]);

    // DnD sensors (shared with drag-drop reorder in step 3).
    const dndSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    // Wizard row/col reorder handlers — reorder the picked arrays AND remap
    // any values the merchant already typed in.
    const handleWizardRowDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = orderedSizes.indexOf(active.id);
        const to = orderedSizes.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const next = arrayMove(orderedSizes, from, to);
        setPickedSizes(next);
        setValues((prev) => remapValuesAfterReorder(prev, orderedSizes, next, "row"));
    };
    const handleWizardColDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldNames = orderedMeasures.map((m) => m.name);
        const from = oldNames.indexOf(active.id);
        const to = oldNames.indexOf(over.id);
        if (from < 0 || to < 0) return;
        const nextNames = arrayMove(oldNames, from, to);
        setPickedMeasures(nextNames);
        setValues((prev) => remapValuesAfterReorder(prev, oldNames, nextNames, "col"));
    };

    const toggleSize = (sz) => {
        setPickedSizes((arr) => arr.includes(sz) ? arr.filter((x) => x !== sz) : [...arr, sz]);
    };
    const toggleMeasure = (name) => {
        setPickedMeasures((arr) => arr.includes(name) ? arr.filter((x) => x !== name) : [...arr, name]);
    };
    const pickAllSizes = () => selectedSeries && setPickedSizes([...selectedSeries.sizes]);
    const pickAllMeasures = () => selectedGroup && setPickedMeasures(selectedGroup.measures.map((m) => m.name));

    const setCell = (key, val) => setValues((v) => ({ ...v, [key]: val }));
    const handleCellKey = (e, ri, ci, maxRi) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const next = ri + 1 < maxRi ? cellRefs.current[`${ri + 1}-${ci}`] : null;
            next?.focus();
            next?.select();
        }
    };

    if (!product && !standalone) return null;

    const linkedTable = linkedTableId ? findTable(tables, linkedTableId) : null;

    // Create the table in the store (create mode) and link every product the
    // merchant has queued up. Awaits the /tables POST before firing any
    // linkProduct calls, otherwise the backend rejects with "Tablo bulunamadı"
    // because the table doesn't exist yet.  For product-triggered wizards
    // `pickedProductIds` is pre-seeded with the current product (see the
    // "Yeni tablo oluştur" click handler in the choice screen).
    const persistTable = async () => {
        const next = addTable({
            name: tableName.trim(),
            seriesLabel: selectedSeries?.name || "",
            groupLabel: selectedGroup?.name || "",
            sourceSeriesId: selectedSeries?.id || null,
            sourceGroupId: selectedGroup?.id || null,
            sizes: orderedSizes,
            measures: orderedMeasures.map((m) => m.name),
            values: gridToValueMap(values, orderedSizes, orderedMeasures.map((m) => m.name)),
            active: true,
        });
        try {
            await next.persistPromise;
        } catch (_) {
            return next; // toast + refresh already handled by store
        }
        Array.from(pickedProductIds).forEach((pid) => linkProduct(pid, next.id));
        return next;
    };

    const handleSave = async () => {
        if (saving) return; // hard guard against double-invoke
        if (mode === "link") {
            if (!linkedTableId) return;
            setSaving(true);
            try {
                linkProduct(product.id, linkedTableId);
                toast.success(`${product.id}, "${linkedTable.name}" tablosuna bağlandı`);
                handleClose();
            } finally {
                setSaving(false);
            }
            return;
        }
        if (!tableName.trim()) {
            toast.error("Tabloya bir ad ver — tablo bu adla listelenecek");
            return;
        }
        const isEmpty = Object.values(values).every((v) => !v || v === "");
        if (isEmpty && !confirmEmpty) {
            setConfirmEmpty(true);
            return;
        }
        setSaving(true);
        try {
            const saved = await persistTable();
            const linkCount = pickedProductIds.size;
            toast.success(
                linkCount
                    ? `"${saved.name}" kaydedildi · ${linkCount} ürün bağlandı`
                    : `"${saved.name}" taslak olarak kaydedildi — yayınlamayı unutma`
            );
            handleClose();
        } finally {
            setSaving(false);
        }
    };

    const canGoNext =
        (step === 1 && seriesId && pickedSizes.length > 0) ||
        (step === 2 && groupId && pickedMeasures.length > 0);

    return (
        <>
            <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
                <SheetContent side="right" className="w-full sm:max-w-[680px] p-0 gap-0 flex flex-col overflow-hidden" data-testid="add-table-wizard">
                    <div className="px-6 py-5 border-b border-border">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                {product ? (
                                    <>
                                        <div className="h-10 w-10 rounded-md border border-border bg-muted overflow-hidden shrink-0">
                                            {product.image && <img src={product.image} alt="" className="h-full w-full object-cover" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">Beden tablosu ekleniyor</div>
                                            <SheetTitle className="font-display text-lg font-bold tracking-tight truncate text-foreground">{product.name}</SheetTitle>
                                            <div className="text-xs font-mono text-muted-foreground">{product.id}</div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="h-10 w-10 rounded-md border border-border bg-muted/40 grid place-items-center shrink-0">
                                            <Table2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">Bağımsız tablo</div>
                                            <SheetTitle className="font-display text-lg font-bold tracking-tight text-foreground">Yeni beden tablosu</SheetTitle>
                                            <div className="text-xs text-muted-foreground">Şimdi hazırla, ürün eklendiğinde eşleştir</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {(standalone || mode === "create") && (
                            <div className="mt-4">
                                <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">Tablo adı <span className="text-destructive">*</span></span>
                                <Input
                                    autoFocus={standalone}
                                    value={tableName}
                                    onChange={(e) => setTableName(e.target.value)}
                                    placeholder="örn. Kadın Üst Giyim — Standart"
                                    className="h-9 text-sm mt-1.5"
                                    data-testid="standalone-table-name"
                                />
                            </div>
                        )}
                        {mode === "create" && (
                            <div className="mt-5 flex items-center gap-3">
                                {[
                                    { n: 1, label: "Beden seç" },
                                    { n: 2, label: "Ölçü seç" },
                                    { n: 3, label: "Değerler" },
                                ].map((s, i, arr) => (
                                    <div key={s.n} className="flex items-center gap-3 flex-1">
                                        <div className={`flex items-center gap-2 ${step >= s.n ? "" : "opacity-50"}`}>
                                            <div className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-display font-bold ${
                                                step > s.n ? "bg-success text-white" :
                                                step === s.n ? "bg-foreground text-background" :
                                                "bg-muted text-muted-foreground"
                                            }`}>
                                                {step > s.n ? <Check className="h-3 w-3" strokeWidth={2.5} /> : s.n}
                                            </div>
                                            <span className={`text-xs font-medium ${step === s.n ? "" : "text-muted-foreground"}`}>{s.label}</span>
                                        </div>
                                        {i < arr.length - 1 && <div className={`flex-1 h-px ${step > s.n ? "bg-foreground/40" : "bg-border"}`} />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-5 flex-1 overflow-y-auto scroll-thin">
                        {mode === "choice" && (
                            <div className="space-y-3" data-testid="wizard-mode-choice">
                                <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground mb-2">
                                    Nasıl ilerlemek istersin?
                                </div>
                                <button
                                    onClick={() => setMode("link")}
                                    className="w-full text-left rounded-xl border border-border bg-surface p-5 hover:border-foreground/30 transition-colors group"
                                    data-testid="mode-link"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="h-10 w-10 rounded-md bg-brand text-brand-fg grid place-items-center shrink-0">
                                            <Link2 className="h-4 w-4" strokeWidth={1.75} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-display text-base font-bold tracking-tight">Mevcut tabloya bağla</h3>
                                                <Badge variant="outline" className="text-[9px] uppercase tracking-wider">Önerilen</Badge>
                                            </div>
                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                Aynı bedenleri ve ölçüleri kullanan bir tablo varsa onu paylaş. Tek değişiklik bütün ürünleri etkiler — bakım maliyetin azalır.
                                            </p>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground self-center" />
                                    </div>
                                </button>
                                <button
                                    onClick={() => {
                                        setMode("create");
                                        if (product) {
                                            if (!tableName.trim()) setTableName(product.name);
                                            setPickedProductIds(new Set([product.id]));
                                        }
                                    }}
                                    className="w-full text-left rounded-xl border border-border bg-surface p-5 hover:border-foreground/30 transition-colors group"
                                    data-testid="mode-create"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="h-10 w-10 rounded-md bg-muted text-foreground grid place-items-center shrink-0 border border-border">
                                            <Plus className="h-4 w-4" strokeWidth={1.75} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-display text-base font-bold tracking-tight mb-1">Yeni tablo oluştur</h3>
                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                Sözlüğünden bedenleri ve ölçüleri seç, cm değerlerini gir. Aynı tabloyu birden fazla ürüne bağlayabilirsin.
                                            </p>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground self-center" />
                                    </div>
                                </button>
                                {product && (
                                    <button
                                        onClick={async () => {
                                            if (saving) return;
                                            const nextExempt = !product.sizingTableExempt;
                                            // Safety guard — if we're MARKING exempt on a product that
                                            // currently has a table linked, this action would silently
                                            // clear the link. Show the same confirmation shape used in
                                            // bulk mode so single vs bulk behaviour stays consistent.
                                            if (nextExempt && links[product.id]) {
                                                setConfirmSingleExempt(true);
                                                return;
                                            }
                                            setSaving(true);
                                            try {
                                                applyExemptToggle(nextExempt);
                                            } finally {
                                                setSaving(false);
                                            }
                                        }}
                                        disabled={saving}
                                        className={`w-full text-left rounded-xl border p-5 hover:border-foreground/30 transition-colors group disabled:opacity-60 disabled:pointer-events-none ${product.sizingTableExempt ? "border-foreground/30 bg-muted/40" : "border-dashed border-border bg-surface"}`}
                                        data-testid="mode-exempt"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 border border-border ${product.sizingTableExempt ? "bg-foreground text-background" : "bg-muted/60 text-muted-foreground"}`}>
                                                <CircleSlash className="h-4 w-4" strokeWidth={1.75} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-display text-base font-bold tracking-tight">
                                                        {product.sizingTableExempt ? "Muaf işaretini kaldır" : "Beden tablosu gerekmez"}
                                                    </h3>
                                                    {product.sizingTableExempt && <Badge variant="outline" className="text-[9px] uppercase tracking-wider">Şu an aktif</Badge>}
                                                </div>
                                                <p className="text-xs text-muted-foreground leading-relaxed">
                                                    {product.sizingTableExempt
                                                        ? <>Bu ürün şu anda &ldquo;beden tablosu gerekmiyor&rdquo; olarak işaretli. Kaldırırsan &ldquo;Tablosu yok&rdquo; listesine geri döner ve tablo bekleyen ürünler arasında yer alır.</>
                                                        : <>Aksesuar, kozmetik, parfüm gibi bedeni olmayan ürünler için. &ldquo;Tablosu yok&rdquo; listesinden çıkar; istediğin zaman geri alabilirsin.</>
                                                    }
                                                </p>
                                            </div>
                                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground self-center" />
                                        </div>
                                    </button>
                                )}
                            </div>
                        )}

                        {mode === "link" && (
                            <div className="space-y-3" data-testid="wizard-link">
                                <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground mb-2">
                                    Hangi tabloya bağlamak istersin?
                                </div>
                                <div className="relative">
                                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} placeholder="Tablo veya seri ara…" className="h-9 pl-9 text-xs bg-background" data-testid="link-table-search" />
                                </div>
                                <div className="space-y-2">
                                    {tables
                                        .filter((t) => {
                                            const q = linkQuery.trim().toLowerCase();
                                            if (!q) return true;
                                            return t.name.toLowerCase().includes(q) || t.seriesLabel.toLowerCase().includes(q) || t.groupLabel.toLowerCase().includes(q);
                                        })
                                        .map((t) => {
                                        const selected = linkedTableId === t.id;
                                        const cnt = countLinked(links, t.id);
                                        return (
                                            <button
                                                key={t.id}
                                                onClick={() => setLinkedTableId(t.id)}
                                                className={`w-full text-left rounded-lg border p-4 transition-all ${selected ? "border-foreground/40 bg-muted/40 ring-1 ring-foreground/10" : "border-border bg-surface hover:border-foreground/20"}`}
                                                data-testid={`link-option-${t.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex items-start gap-3 min-w-0">
                                                        <Table2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.75} />
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-display font-bold tracking-tight">{t.name}</span>
                                                                {cnt > 0 && <Badge variant="outline" className="text-[9px]">{cnt} ürün</Badge>}
                                                                {cnt === 0 && <Badge variant="outline" className="text-[9px] border-warning/40 text-warning bg-warning-soft">eşleşme bekliyor</Badge>}
                                                            </div>
                                                            <div className="text-[10px] text-muted-foreground mt-1.5 font-mono">{t.seriesLabel} · {t.groupLabel}</div>
                                                        </div>
                                                    </div>
                                                    <div className={`h-5 w-5 rounded-full grid place-items-center shrink-0 ${selected ? "bg-foreground" : "border border-border"}`}>
                                                        {selected && <Check className="h-3 w-3 text-background" strokeWidth={3} />}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {mode === "create" && step === 1 && (
                            <WizardStep1
                                series={series}
                                seriesId={seriesId}
                                onPickSeries={(id) => { setSeriesId(id); setPickedSizes([]); }}
                                pickedSizes={pickedSizes}
                                onToggleSize={toggleSize}
                                onPickAll={pickAllSizes}
                                onClearAll={() => setPickedSizes([])}
                                selectedSeries={selectedSeries}
                            />
                        )}

                        {mode === "create" && step === 2 && (
                            <WizardStep2
                                groups={groups}
                                groupId={groupId}
                                onPickGroup={(id) => { setGroupId(id); setPickedMeasures([]); }}
                                pickedMeasures={pickedMeasures}
                                onToggleMeasure={toggleMeasure}
                                onPickAll={pickAllMeasures}
                                onClearAll={() => setPickedMeasures([])}
                                selectedGroup={selectedGroup}
                            />
                        )}

                        {mode === "create" && step === 3 && selectedSeries && selectedGroup && (
                            <div className="space-y-4" data-testid="wizard-step-3">
                                {/* Redesigned Akıllı Doldur card — bigger, richer explanation,
                                    two ways to fill (Excel paste + auto-fill button). */}
                                <div className="rounded-xl border border-brand/25 bg-brand/5 overflow-hidden" data-testid="wizard-smart-fill-card">
                                    <div className="px-4 py-3.5 flex items-start gap-3">
                                        <div className="h-9 w-9 rounded-lg bg-brand text-brand-fg grid place-items-center shrink-0">
                                            <Sparkles className="h-4 w-4" strokeWidth={2} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-foreground">Hızlı doldur</div>
                                            <p className="text-[11.5px] text-muted-foreground mt-0.5 leading-relaxed">
                                                Excel&apos;den <span className="font-medium text-foreground">bloğu kopyalayıp</span> aşağıdaki tablonun herhangi bir hücresine yapıştır — tablo otomatik dolar. Ya da her ölçüde en az <span className="font-medium text-foreground">2 değer</span> yaz ve <span className="font-medium text-foreground">Akıllı doldur</span>&apos;a bas; kalan hücreler artış adımına göre tamamlanır.
                                            </p>
                                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                                <Button
                                                    size="sm"
                                                    className="h-8 text-xs gap-1.5 bg-brand text-brand-fg hover:bg-brand/90"
                                                    onClick={() => {
                                                        const { next, filled } = gridSmartFill(values, orderedSizes.length, orderedMeasures.length);
                                                        if (!filled) { toast.info("Otomatik doldurma için her ölçüde en az 2 değer gir"); return; }
                                                        setValues(next);
                                                        toast.success(`${filled} hücre artış adımına göre dolduruldu`);
                                                    }}
                                                    data-testid="wizard-smart-fill"
                                                >
                                                    <Sparkles className="h-3 w-3" /> Akıllı doldur
                                                </Button>
                                                <span className="text-[10.5px] text-muted-foreground inline-flex items-center gap-1.5">
                                                    <FileSpreadsheet className="h-3 w-3" /> Excel bloğu kopyala → hücreye yapıştır
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-border overflow-hidden">
                                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleWizardColDragEnd}>
                                        <SortableContext items={orderedMeasures.map((m) => m.name)} strategy={horizontalListSortingStrategy}>
                                            <div className="grid bg-muted/40 text-[10px] font-semibold tracking-wider uppercase text-muted-foreground border-b border-border" style={{ gridTemplateColumns: `80px repeat(${orderedMeasures.length}, minmax(0,1fr))` }}>
                                                <div className="px-3 py-2">Beden</div>
                                                {orderedMeasures.map((m, ci) => (
                                                    <SortableColHeader key={m.name} id={m.name} className="px-3 py-2 flex items-center gap-1 group">
                                                        {({ attributes, listeners }) => (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    {...attributes}
                                                                    {...listeners}
                                                                    className="h-4 w-3.5 -ml-1 shrink-0 flex items-center justify-center text-muted-foreground/40 hover:text-foreground cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    aria-label={`${m.name} sütununu sürükle`}
                                                                    title="Sütun sırasını değiştirmek için sürükle"
                                                                    data-testid={`wizard-drag-measure-${ci}`}
                                                                >
                                                                    <GripVertical className="h-3 w-3" strokeWidth={1.75} />
                                                                </button>
                                                                <span className="truncate flex-1">{m.name} <span className="lowercase text-muted-foreground/60">(cm)</span></span>
                                                            </>
                                                        )}
                                                    </SortableColHeader>
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleWizardRowDragEnd}>
                                        <SortableContext items={orderedSizes} strategy={verticalListSortingStrategy}>
                                            {orderedSizes.map((sz, ri) => (
                                                <SortableGridRow
                                                    key={sz}
                                                    id={sz}
                                                    className="grid items-center border-b border-border last:border-0 text-sm hover:bg-muted/30 group bg-background"
                                                    style={{ gridTemplateColumns: `80px repeat(${orderedMeasures.length}, minmax(0,1fr))` }}
                                                >
                                                    {({ attributes, listeners }) => (
                                                        <>
                                                            <div className="px-2 py-2 flex items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    {...attributes}
                                                                    {...listeners}
                                                                    className="h-5 w-3.5 shrink-0 flex items-center justify-center text-muted-foreground/40 hover:text-foreground cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    aria-label={`${sz} satırını sürükle`}
                                                                    title="Satır sırasını değiştirmek için sürükle"
                                                                    data-testid={`wizard-drag-size-${ri}`}
                                                                >
                                                                    <GripVertical className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                                </button>
                                                                <span className="font-display font-bold flex-1 truncate">{sz}</span>
                                                            </div>
                                                            {orderedMeasures.map((m, ci) => (
                                                                <div key={`${sz}-${m.name}`} className="px-2 py-1.5">
                                                                    <input
                                                                        ref={(el) => { cellRefs.current[`${ri}-${ci}`] = el; }}
                                                                        type="number"
                                                                        placeholder="—"
                                                                        value={values[`${ri}-${ci}`] || ""}
                                                                        onChange={(e) => setCell(`${ri}-${ci}`, e.target.value)}
                                                                        onPaste={(e) => {
                                                                            const text = e.clipboardData.getData("text");
                                                                            if (!text || (!text.includes("\t") && !text.includes("\n") && !text.includes(";"))) return;
                                                                            e.preventDefault();
                                                                            const { next, filled } = gridPaste(values, text, ri, ci, orderedSizes.length, orderedMeasures.length);
                                                                            setValues(next);
                                                                            toast.success(`${filled} hücre yapıştırıldı`);
                                                                        }}
                                                                        onKeyDown={(e) => handleCellKey(e, ri, ci, orderedSizes.length)}
                                                                        className="w-full h-8 bg-background border border-transparent hover:border-border focus:border-foreground/30 rounded-md px-2 text-sm font-mono num outline-none placeholder:text-muted-foreground/40"
                                                                        data-testid={`cell-${ri}-${ci}`}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </SortableGridRow>
                                            ))}
                                        </SortableContext>
                                    </DndContext>
                                </div>
                                <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                                    <Info className="h-3 w-3" /> Satır veya sütunu <b className="mx-0.5">sürükleyerek</b> sırasını değiştir · <kbd className="font-mono text-[10px] px-1 py-0.5 rounded border border-border bg-muted">Enter</kbd> bir alt satır · <kbd className="font-mono text-[10px] px-1 py-0.5 rounded border border-border bg-muted">Tab</kbd> bir sonraki ölçü
                                </p>

                                {/* Bağlı Ürünler — same UX as the edit drawer, but picked
                                    products aren't linked until the table is saved.
                                    Product-triggered wizards pre-select the current product. */}
                                {mode === "create" && (
                                    <WizardLinkedProducts
                                        pickedIds={pickedProductIds}
                                        onOpenModal={() => setLinkModalOpen(true)}
                                        products={products}
                                        links={links}
                                        tables={tables}
                                        onDetach={(pid) => setPickedProductIds((prev) => {
                                            const n = new Set(prev);
                                            n.delete(pid);
                                            return n;
                                        })}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={() => {
                                if (mode === "choice") return handleClose();
                                if (mode === "link") return setMode("choice");
                                if (mode === "create" && step === 1) return standalone ? handleClose() : setMode("choice");
                                setStep((s) => s - 1);
                            }}
                            className="h-9 text-xs gap-1.5"
                        >
                            <ArrowLeft className="h-3 w-3" /> {mode === "choice" || (standalone && mode === "create" && step === 1) ? "İptal" : "Geri"}
                        </Button>

                        <div className="flex items-center gap-2">
                            {mode === "create" && <span className="text-[11px] font-mono text-muted-foreground">Adım {step}/3</span>}

                            {mode === "choice" && (
                                <span className="text-[11px] text-muted-foreground">Bir seçenek seç</span>
                            )}

                            {mode === "link" && (
                                <Button
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={!linkedTableId || saving}
                                    className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg disabled:opacity-60 disabled:pointer-events-none"
                                    data-testid="wizard-link-save"
                                >
                                    {saving ? (
                                        <><Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…</>
                                    ) : (
                                        <><Check className="h-3 w-3" /> Bu tabloya bağla</>
                                    )}
                                </Button>
                            )}

                            {mode === "create" && step < 3 && (
                                <Button
                                    size="sm"
                                    onClick={() => setStep((s) => s + 1)}
                                    disabled={!canGoNext}
                                    className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg"
                                    data-testid="wizard-next"
                                >
                                    Devam et <ArrowRight className="h-3 w-3" />
                                </Button>
                            )}

                            {mode === "create" && step === 3 && (
                                <Button
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="h-9 text-xs gap-1.5 bg-brand hover:bg-brand/90 text-brand-fg disabled:opacity-60 disabled:pointer-events-none"
                                    data-testid="wizard-save"
                                >
                                    {saving ? (
                                        <><Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…</>
                                    ) : (
                                        <><Check className="h-3 w-3" /> Tabloyu kaydet</>
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={confirmEmpty} onOpenChange={setConfirmEmpty}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Tablo boş</AlertDialogTitle>
                        <AlertDialogDescription>
                            Hiçbir cm değeri girilmedi. Yine de kaydetmek ister misin? Sonradan da düzenleyebilirsin.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={saving}>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={saving}
                            onClick={async () => {
                                if (saving) return;
                                setSaving(true);
                                try {
                                    setConfirmEmpty(false);
                                    const saved = await persistTable();
                                    toast.success(`Boş tablo kaydedildi · ${saved.name}`);
                                    handleClose();
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            className="disabled:opacity-60 disabled:pointer-events-none"
                        >
                            {saving ? "Kaydediliyor…" : "Yine de kaydet"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Ürün bağlama akışı — aynı modal, ama pending state olarak toplanıyor.
                Tablo save edilene kadar `linkProduct` çağrılmıyor. */}
            <LinkProductsModal
                open={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                table={{ id: null, name: tableName.trim() || "Yeni tablo" }}
                products={products.filter((p) => !pickedProductIds.has(p.id))}
                links={links}
                tables={tables}
                allowCommitWithoutMove={true}
                onCommit={(ids) => {
                    setPickedProductIds((prev) => {
                        const n = new Set(prev);
                        ids.forEach((id) => n.add(id));
                        return n;
                    });
                    toast.success(`${ids.length} ürün seçildi — Kaydet'e bastığında tabloya bağlanacak`);
                    setLinkModalOpen(false);
                }}
            />

            {/* Single-product Muaf SAFETY — surfaced only when the merchant flips
                a *linked* product to Muaf, because that action silently clears
                the tableId server-side. Mirrors the bulk-mode confirmation for
                consistency (same title, same "Vazgeç / Muaf yap" split). */}
            {product && (
                <AlertDialog open={confirmSingleExempt} onOpenChange={(o) => !o && setConfirmSingleExempt(false)}>
                    <AlertDialogContent data-testid="single-muaf-confirm">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Muaf işaretle · Tablo bağlantısı kaldırılacak</AlertDialogTitle>
                            <AlertDialogDescription>
                                <span className="font-medium text-foreground">{product.name || product.id}</span> şu an{" "}
                                <span className="font-medium text-warning">
                                    &ldquo;{findTable(tables, links[product.id])?.name || "bir tablo"}&rdquo;
                                </span>{" "}
                                tablosuna bağlı. Muaf yaparsan bu bağlantı kaldırılır —{" "}
                                <span className="text-foreground">tablonun kendisi silinmez</span>, sadece bu ürünle olan bağı gider.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="single-muaf-cancel">Vazgeç</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    setConfirmSingleExempt(false);
                                    applyExemptToggle(true);
                                }}
                                data-testid="single-muaf-confirm-action"
                            >
                                Muaf yap, bağı kaldır
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </>
    );
};
